import AnimalGroupStatus from "@wesstron/utils/group-status/AnimalGroupStatus";
import {convertFarmTimeToUTCHelper} from "@wesstron/utils/utils/TimeUtils";
import memoizeOne from "memoize-one";
import moment from "moment";
import {
    editAnimalGroups,
    updateAnimalGroupsNotificationFailure,
    updateAnimalGroupsNotificationSuccess,
} from "../../../api/animalGroups/editAnimalGroups";
import {findLast, sortBy} from "lodash";

export function submit(values, dispatch, props) {
    const {insertions, _Comment} = values;
    const {mergedInsertions: {insertionsIDs = []} = {}, group: {AnmGrp} = {}} =
        props;
    const insertionsToChange = {};
    Object.entries(insertions).forEach(([key, value]) => {
        if (key.startsWith("mergedInsertions")) {
            const insertionDetails = key.split("_");
            insertionsToChange[
                `${insertionDetails[1]}_${insertionDetails[2]}`
            ] = value;
        }
    });
    const row = {
        AnmGrp,
        insertionsIDs,
        insertionsToChange,
        updateAnimals: false,
    };
    if (_Comment) {
        row.comment = _Comment;
    }
    return editAnimalGroups([row])
        .then((res) => {
            updateAnimalGroupsNotificationSuccess(res);
            props.handleHide();
        })
        .catch((err) => {
            updateAnimalGroupsNotificationFailure(err);
        });
}

const getGroupStatus = memoizeOne(
    (group, events, farm) => new AnimalGroupStatus(group, events, farm)
);

const getInsertionEventsByKey = memoizeOne((events, insertionsIDs) => {
    const tmp = {};
    for (const [key, EvIDs] of Object.entries(insertionsIDs)) {
        const insertionEvent = events.find((ev) => EvIDs.includes(ev.EvID));
        if (insertionEvent) {
            tmp[`mergedInsertions_${key}`] = insertionEvent;
        }
    }
    return tmp;
});

/**
 * `AnimalGroupStatus.validate` sprawdza tylko sume calej lokalizacji, wiec inne zwierze
 * stojace w tym samym miejscu potrafi zamaskowac ujemny stan konkretnego wprowadzenia.
 * Dlatego skladamy os czasu stanu pojedynczego AnmID ze wszystkich lokalizacji.
 */
const getAnimalBalanceTimeline = (groupStatus, AnmID) => {
    const locations = Object.values(groupStatus.data.locations);
    const dates = new Set();
    for (let rows of locations) {
        for (let row of rows) {
            dates.add(row.date);
        }
    }
    return sortBy([...dates]).map((date) => ({
        date,
        amount: locations.reduce((sum, rows) => {
            const row = findLast(rows, (item) => item.date <= date);
            return sum + (row?.AnmIDs?.[AnmID] || 0);
        }, 0),
    }));
};

const isAnimalBalanceValid = (timeline, changes) =>
    timeline.every(({date, amount}) => {
        const change = changes.reduce(
            (sum, item) => (item.date <= date ? sum + item.amount : sum),
            0
        );
        return amount + change >= 0;
    });

export function validate(values, props) {
    const {
        t,
        group,
        farm,
        events = [],
        mergedInsertions: {
            insertions: initialInsertions = {},
            insertionsIDs = {},
        } = {},
    } = props;
    const {insertions = {}} = values;
    const errors = {
        insertions: {},
    };
    const mergedInsertionKeys = Object.keys(insertions).filter((key) =>
        key.startsWith("mergedInsertions")
    );
    if (!group || !farm) return errors;
    const insertionEventsByKey = getInsertionEventsByKey(events, insertionsIDs);
    // zmiany ilosci wstawien pogrupowane po lokalizacji, w ktorej wstawienie mialo miejsce
    const validationRowsByLocation = {};
    // te same zmiany pogrupowane po zwierzeciu, ktorego wstawienie dotyczy
    const validationRowsByAnimal = {};
    for (let key of mergedInsertionKeys) {
        const currentValue = insertions[key];
        if (currentValue <= 0) {
            errors.insertions[key] = t("errors.noLessOrEq0");
            continue;
        }
        const diff = currentValue - (initialInsertions[key] || 0);
        if (diff === 0) continue;
        const insertionEvent = insertionEventsByKey[key];
        if (!insertionEvent) {
            console.warn(
                "Stało się coś, czego nie oczekiwałem",
                key,
                insertionsIDs
            );
            continue;
        }
        const location = insertionEvent.EvData.PlcmntID;
        const date = +moment
            .utc(
                convertFarmTimeToUTCHelper(insertionEvent.EvTime, farm.Timezone)
            )
            .startOf("day");
        const {AnmID} = insertionEvent;
        const validationRow = {date, amount: diff, key};
        if (!validationRowsByLocation[location]) {
            validationRowsByLocation[location] = [];
        }
        validationRowsByLocation[location].push(validationRow);
        if (!validationRowsByAnimal[AnmID]) {
            validationRowsByAnimal[AnmID] = [];
        }
        validationRowsByAnimal[AnmID].push(validationRow);
    }
    const groupStatus = getGroupStatus(group, events, farm);
    let hasHistoryIssue = false;
    // stan samego zwierzecia nie moze zejsc ponizej zera - inne zwierzeta w tej samej
    // lokalizacji nie moga "pokryc" niedoboru wynikajacego ze zmiany wprowadzenia
    for (const [AnmID, rows] of Object.entries(validationRowsByAnimal)) {
        const timeline = getAnimalBalanceTimeline(groupStatus, AnmID);
        // dokladamy zmiany po kolei, aby wskazac ktore wprowadzenie zepsulo stany
        const validationRows = [];
        for (let row of sortBy(rows, "date")) {
            validationRows.push(row);
            if (!isAnimalBalanceValid(timeline, validationRows)) {
                errors.insertions[row.key] = t("sumOfInsertionError");
                validationRows.pop();
                hasHistoryIssue = true;
            }
        }
    }
    for (const [location, rows] of Object.entries(validationRowsByLocation)) {
        const validationRows = [];
        for (let row of sortBy(rows, "date")) {
            if (errors.insertions[row.key]) continue;
            validationRows.push(row);
            if (!groupStatus.validate(location, validationRows)) {
                validationRows.pop();
                hasHistoryIssue = true;
            }
        }
    }
    if (hasHistoryIssue) {
        errors._error = t("sumOfInsertionError");
    }
    return errors;
}
